import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { META, STATS, FLEET } from './data';

// HOME — full-bleed cinematic seascape hero (very different from Watch's text-only hero)
// Followed by a HORIZONTAL SCROLL fleet carousel and a stats panel.
export default function Home({ setPage }) {
  return (
    <div className="bg-[#0A1F2E] text-[#E8DDC7]">
      {/* Cinematic seascape hero */}
      <section className="relative h-screen min-h-[700px] overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/189349/pexels-photo-189349.jpeg?auto=compress&cs=tinysrgb&w=2200" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F2E]/40 via-transparent to-[#0A1F2E]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F2E]/70 via-[#0A1F2E]/0 to-[#0A1F2E]/40" />
        </div>
        <div className="relative h-full max-w-[1440px] mx-auto px-8 lg:px-14 flex flex-col justify-end pb-20 z-10">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.4 }}>
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B68F4E] mb-6">— Antibes · since {META.est}</p>
            <h1 className="font-serif text-[58px] sm:text-[88px] lg:text-[140px] leading-[0.88] tracking-[-0.025em] max-w-5xl">
              The sea, <span className="italic text-[#B68F4E]">privately</span>.
            </h1>
            <p className="text-lg lg:text-xl text-[#E8DDC7]/75 max-w-xl mt-10 leading-[1.6]">{META.motto}</p>
            <div className="mt-12 flex items-center gap-6">
              <button onClick={() => setPage('fleet')} className="bg-[#B68F4E] text-[#0A1F2E] px-9 py-4 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#E8DDC7] transition-colors flex items-center gap-3" data-testid="yacht-home-fleet-cta">
                Visit the fleet <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
              </button>
              <button onClick={() => setPage('voyage')} className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#E8DDC7]/80 underline-offset-8 hover:text-[#B68F4E] hover:underline">
                Read a sample voyage
              </button>
            </div>
          </motion.div>
        </div>
        {/* hero meta strip — bottom edge */}
        <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-[#E8DDC7]/10 backdrop-blur-sm bg-[#0A1F2E]/40">
          <div className="max-w-[1440px] mx-auto px-8 lg:px-14 py-3 flex items-center justify-between font-mono text-[10px] tracking-[0.3em] uppercase">
            <span className="text-[#E8DDC7]/55">N 43° 35′ &nbsp; E 7° 07′</span>
            <span className="text-[#B68F4E] hidden md:block">Aurora · 54.2 m · sailing</span>
            <span className="text-[#E8DDC7]/55">Wind 11 kts · NE</span>
          </div>
        </div>
      </section>

      {/* Horizontal-scroll fleet — distinctive vs Watch's vertical archive grid */}
      <section className="bg-[#0A1F2E] py-24 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-14 flex items-end justify-between mb-12 gap-6">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B68F4E] mb-3">— Twelve hulls, three temperaments</p>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[0.95]">A fleet kept <span className="italic text-[#B68F4E]">small</span>, on purpose.</h2>
          </div>
          <button onClick={() => setPage('fleet')} className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#B68F4E] hidden md:flex items-center gap-2 hover:text-[#E8DDC7]">All twelve <ArrowRight className="w-3 h-3" /></button>
        </div>
        <div className="overflow-x-auto pb-6 [scrollbar-width:thin] [scrollbar-color:#B68F4E_#0A1F2E]">
          <div className="flex gap-6 px-8 lg:px-14 w-max">
            {FLEET.map((f, i) => (
              <motion.button key={f.name} onClick={() => setPage('fleet')} initial={{ opacity: 0, x: 60 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className="w-[420px] lg:w-[520px] flex-shrink-0 group text-left">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={f.img} alt={f.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F2E]/85 via-[#0A1F2E]/0 to-[#0A1F2E]/30" />
                  <span className="absolute top-5 left-5 font-mono text-[9px] tracking-[0.4em] uppercase text-[#B68F4E] border border-[#B68F4E]/60 px-2.5 py-1">{f.class}</span>
                  <div className="absolute bottom-6 left-6 right-6">
                    <p className="font-serif text-3xl lg:text-4xl text-[#E8DDC7] leading-tight">{f.name}</p>
                    <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#B68F4E] mt-2">{f.loa} · {f.guests} guests · crew {f.crew}</p>
                  </div>
                </div>
                <p className="font-mono text-[11px] text-[#E8DDC7]/55 mt-4 group-hover:text-[#B68F4E] transition-colors">{f.rate} →</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Stats panel — large numbers, no images */}
      <section className="border-y border-[#E8DDC7]/10 bg-[#06151E]">
        <div className="max-w-[1440px] mx-auto px-8 lg:px-14 py-20 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map(s => (
            <div key={s.l} className="border-l-2 border-[#B68F4E]/40 pl-6">
              <p className="font-serif text-5xl lg:text-7xl text-[#B68F4E] leading-none">{s.v}</p>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E8DDC7]/55 mt-3">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Captain's letter — narrow column, type-driven */}
      <section className="py-24 lg:py-32 max-w-[820px] mx-auto px-8">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B68F4E] mb-6">— A letter from the bureau</p>
        <p className="font-serif text-2xl lg:text-3xl leading-[1.5] text-[#E8DDC7]/90 italic">
          &ldquo;A charter is not a hotel that floats. It is a small fortress of the senses, kept by a captain who has met you before. Our job is to introduce you both — and then to disappear.&rdquo;
        </p>
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#E8DDC7]/45 mt-10">Bénédicte Avalon &middot; Bureau Director, since 1992</p>
      </section>
    </div>
  );
}
